/* eslint-disable no-undef */
import { Navbar, TesSearch, Input } from "components";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getLongDate } from "utils";
import LogoLoader from "components/widgets/loader/LogoLoader";
import { ISchools } from "types";
import Select, { SingleValue } from "react-select";
import { zones } from "pages/updateProfile/DropDownOptions";
import { downloadLogo } from "assets/logos";
import { useFetchSchoolsQuery } from "services/schoolApi";

const ITEMS_PER_PAGE = 9;

export const SchoolView: React.FC = () => {
  const { data: schoolsResponse, isLoading, isError } = useFetchSchoolsQuery();
  console.log(schoolsResponse);

  // Extract schools array from API response - schools are in DATA.programs
  const schools = (schoolsResponse ?? []) as ISchools[];
  console.log(schools);

  // Debug logging
  useEffect(() => {
    console.log("Schools Response:", schoolsResponse);
    console.log("Extracted Schools:", schools);
    console.log("Is Loading:", isLoading);
    console.log("Is Error:", isError);
  }, [schoolsResponse, schools, isLoading, isError]);

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [openSchoolId, setOpenSchoolId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPrevNext, setShowPrevNext] = useState<boolean>(false);
  const [currentSchoolIndex, setCurrentSchoolIndex] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openStaffId, setOpenStaffId] = useState<string | null>(null);
  const [staffSearch, setStaffSearch] = useState<string>("");
  const [openLeaderRole, setOpenLeaderRole] = useState<string | null>(null);

  // New state for showing/hiding sections
  const [showLeadership, setShowLeadership] = useState<{ [key: string]: boolean }>({});
  const [showStaffList, setShowStaffList] = useState<{ [key: string]: boolean }>({});

  interface SelectOption {
    value: string;
    label: string;
  }

  useEffect(() => {
    setCurrentPage(1);
    setOpenSchoolId(null);
    setShowPrevNext(false);
    setCurrentSchoolIndex(0);
  }, [selectedZone]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenSchoolId(null);
    setShowPrevNext(false);
    setCurrentSchoolIndex(0);
  };

  const handlePreviousSchool = () => {
    if (currentSchoolIndex > 0) {
      const newIndex = currentSchoolIndex - 1;
      setCurrentSchoolIndex(newIndex);
      setOpenSchoolId(filteredSchools[newIndex]._id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleNextSchool = () => {
    if (currentSchoolIndex < filteredSchools.length - 1) {
      const newIndex = currentSchoolIndex + 1;
      setCurrentSchoolIndex(newIndex);
      setOpenSchoolId(filteredSchools[newIndex]._id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleToggle = (_id: string | undefined) => {
    if (_id) {
      setOpenSchoolId(openSchoolId === _id ? null : _id);
      setShowPrevNext(openSchoolId !== _id);
      if (openSchoolId !== _id) {
        const index = filteredSchools.findIndex((school) => school._id === _id);
        setCurrentSchoolIndex(index);
        const newPage = Math.floor(index / ITEMS_PER_PAGE) + 1;
        setCurrentPage(newPage);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const toggleLeadership = (schoolId: string) => {
    setShowLeadership((prev) => ({
      ...prev,
      [schoolId]: !prev[schoolId]
    }));
  };

  const toggleStaffList = (schoolId: string) => {
    setShowStaffList((prev) => ({
      ...prev,
      [schoolId]: !prev[schoolId]
    }));
  };

  interface InfoRowProps {
    label: string;
    value?: React.ReactNode;
  }

  const InfoRow = ({ label, value }: InfoRowProps) => {
    return (
      <div className="flex items-start gap-3 rounded-md bg-white px-3 py-2 shadow-sm border border-gray-100">
        {/* Vertical Divider */}
        <span className="w-1 rounded-full bg-emerald-500 shrink-0" />

        <div className="leading-tight">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-0.5 text-sm font-semibold text-gray-800 break-words">{value ?? "—"}</p>
        </div>
      </div>
    );
  };

  const filteredSchools = schools?.filter((school) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      school.nameOfSchool.toLowerCase().includes(search) ||
      school.category.toLowerCase().includes(search) ||
      school.location.toLowerCase().includes(search) ||
      school?.listOfStaff?.some(
        (staff) =>
          staff?.staffName?.firstName?.toLowerCase().includes(search) ||
          staff?.ogNumber?.toLowerCase().includes(search)
      );

    const matchesZone = !selectedZone || school.zone === selectedZone;

    return matchesSearch && matchesZone;
  });

  const zonesToUppercase = zones.map((zone) => zone.toUpperCase());
  const zoneOptions: SelectOption[] = zonesToUppercase.map((zone) => ({
    value: zone,
    label: zone
  }));

  if (isLoading) {
    console.log("Loading schools...");
    return <LogoLoader />;
  }

  if (isError) {
    console.log("Error loading schools");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500 text-lg">Error loading schools. Please try again.</p>
      </div>
    );
  }

  if (!schools || schools.length === 0) {
    console.log("No schools data available");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-lg">No schools data available.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(filteredSchools.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSchools = filteredSchools.slice(startIndex, endIndex);

  const handleSelectChange =
    (setter: React.Dispatch<React.SetStateAction<string | null>>) =>
    async (selectedOption: SingleValue<SelectOption>) => {
      const newValue = selectedOption ? selectedOption.value : null;
      setter(newValue);
    };

  return (
    <>
      <Navbar />
      <div className="flex flex-col h-full w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 mt-16 gap-6 p-6 rounded-xl shadow-xl">
        {/* HEADER ROW */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 w-full">
          {/* LEFT: SCHOOLS COUNT */}
          <div className="flex flex-col gap-1">
            <span className="text-sm uppercase tracking-wide text-white">Overview</span>
            <h4 className="text-white text-xl font-semibold">
              Showing
              <span className="mx-2 text-emerald-400 font-bold">{filteredSchools.length}</span>
              of
              <span className="mx-2 text-emerald-400  font-semibold">{schools.length}</span>
              schools, Zonal Offices and PG Offices
            </h4>
          </div>

          {/* MIDDLE: ZONE FILTER */}
          <div className="w-full md:w-[320px]">
            <label htmlFor="zones" className="block text-sm uppercase font-medium text-white mb-2">
              Filter by Zone
            </label>
            <Select
              id="zones"
              options={zoneOptions}
              value={zoneOptions.find((option) => option.value === selectedZone)}
              onChange={handleSelectChange(setSelectedZone)}
              placeholder="Select or search zone"
              isSearchable
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          {/* RIGHT: SEARCH */}
          <div className="relative w-full md:w-[420px]">
            <span className="text-sm uppercase tracking-wide text-white">
              Search by Name or OgNumber
            </span>
            <Input
              size="md"
              onChange={handleChange}
              value={searchTerm}
              placeholder="Search schools by name..."
              className="h-11 w-full rounded-full pl-5 pr-12 text-sm bg-white/95 shadow-md focus:ring-2 focus:ring-emerald-500"
            />
            <TesSearch
              size={20}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>

        {paginatedSchools.length > 0 ? (
          paginatedSchools.map((school) => (
            <div
              key={school._id}
              className={`flex flex-col w-full ${
                openSchoolId && openSchoolId !== school._id ? "hidden" : ""
              }`}
            >
              <div
                className="flex flex-row justify-between items-center hover:bg-[#7ed348] hover:text-black bg-[#1b733f] text-white p-3 cursor-pointer rounded-lg shadow-md"
                onClick={() => {
                  handleToggle(school._id);
                }}
              >
                <p className="text-lg font-semibold">
                  {school?.nameOfSchool.toUpperCase()} {school?.category.toUpperCase()}{" "}
                  {school?.location.toUpperCase()}
                </p>
                <h5 className="text-xl font-bold">{openSchoolId === school._id ? "-" : "+"}</h5>
              </div>

              <AnimatePresence>
                {openSchoolId === school?._id && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden  bg-gold text-[#60cd84] w-full p-4 rounded-lg shadow-lg mt-2"
                  >
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md max-h-[500px] overflow-auto">
                      {/* School Name */}
                      <div className="text-center mb-4">
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1b733f] break-words">
                          {school?.nameOfSchool}
                        </h2>
                      </div>

                      {/* School Meta Info */}
                      <div className="space-y-3 mb-4">
                        {/* First Row - Category, Zone, Division */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm sm:text-base">
                          <p className="text-gray-700">
                            <strong>Category:</strong> {school?.category}
                          </p>
                          <p className="text-gray-700">
                            <strong>Zone:</strong> {school?.zone}
                          </p>
                          <p className="text-gray-700">
                            <strong>Division:</strong> {school?.division}
                          </p>
                          <p className="text-gray-700">
                            <strong>Address:</strong> {school?.address}
                          </p>
                          <p className="text-gray-700">
                            <strong>Location:</strong> {school?.location}
                          </p>
                          <p className="text-gray-700">
                            <strong>Location:</strong> {school?.schoolCode}
                          </p>
                        </div>

                        {/* Third Row - Staff Statistics */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm sm:text-base pt-2 border-t border-gray-200">
                          <p className="text-gray-700">
                            <strong>Total Staff:</strong>{" "}
                            <span className="font-semibold">
                              {school?.listOfStaff?.length || 0}
                            </span>
                          </p>
                          <p className="text-gray-700">
                            <strong>Teaching:</strong>{" "}
                            <span className="font-semibold">
                              {school?.listOfStaff?.filter(
                                (staff) => staff.staffType === "TEACHING"
                              ).length || 0}
                            </span>
                          </p>
                          <p className="text-gray-700">
                            <strong>Non-Teaching:</strong>{" "}
                            <span className="font-semibold">
                              {school?.listOfStaff?.filter(
                                (staff) => staff.staffType === "NON-TEACHING"
                              ).length || 0}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* SCHOOL LEADERSHIP DROPDOWN */}
                    <div className="mt-6 bg-green-100 rounded-2xl shadow-xl border border-gray-700">
                      <button
                        type="button"
                        onClick={() => school._id && toggleLeadership(school._id)}
                        className="w-full flex justify-between items-center p-6 hover:bg-green-200 rounded-2xl transition-colors"
                      >
                        <div>
                          <h5 className="text-xl font-semibold text-emerald-600 tracking-wide">
                            School Leadership
                          </h5>
                          <span className="text-sm text-gray-600">Principal & Vice Principals</span>
                        </div>
                        <span className="text-2xl font-bold text-emerald-600">
                          {showLeadership[school._id] ? "−" : "+"}
                        </span>
                      </button>

                      <AnimatePresence>
                        {showLeadership[school._id] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-0">
                              {/* LEADERS LIST */}
                              <ul className="space-y-3">
                                {[
                                  { key: "principal", title: "Principal", data: school.principal },
                                  {
                                    key: "vp-admin",
                                    title: "Vice Principal (Admin)",
                                    data: school.vicePrincipalAdmin
                                  },
                                  {
                                    key: "vp-academics",
                                    title: "Vice Principal (Academics)",
                                    data: school.vicePrincipalAcademics
                                  }
                                ].map(({ key, title, data }) => (
                                  <li
                                    key={key}
                                    className="bg-white border border-gray-300 rounded-xl shadow-sm"
                                  >
                                    {/* HEADER */}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenLeaderRole(openLeaderRole === key ? null : key)
                                      }
                                      className="w-full flex justify-between items-center px-4 py-3 hover:bg-emerald-50 rounded-xl"
                                    >
                                      <div className="text-left">
                                        <p className="font-semibold text-gray-800">{title}</p>
                                        <p className="text-xs text-gray-500">
                                          {data?.staffName?.firstName || "Vacant"} •{" "}
                                          {data?.ogNumber || "—"}
                                        </p>
                                      </div>

                                      <span className="text-xl font-bold text-emerald-600">
                                        {openLeaderRole === key ? "−" : "+"}
                                      </span>
                                    </button>

                                    {/* DETAILS */}
                                    <AnimatePresence>
                                      {openLeaderRole === key && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: "auto", opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.25 }}
                                          className="overflow-hidden px-4 pb-4"
                                        >
                                          {data && (
                                            <div className="mt-4 space-y-6 text-sm text-gray-700">
                                              {/* ===== BASIC INFORMATION ===== */}
                                              <section>
                                                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                  <span className="h-4 w-1 rounded bg-emerald-600" />
                                                  Personal Information
                                                </h3>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                  <InfoRow
                                                    label="First Name"
                                                    value={data.staffName?.firstName}
                                                  />
                                                  <InfoRow label="Gender" value={data.gender} />
                                                  <InfoRow
                                                    label="Phone Number"
                                                    value={data.phoneNumber}
                                                  />
                                                  <InfoRow
                                                    label="Residential Address"
                                                    value={data.residentialAddress}
                                                  />
                                                  <InfoRow
                                                    label="Date of Birth"
                                                    value={
                                                      data.dateOfBirth
                                                        ? getLongDate(
                                                            new Date(
                                                              data.dateOfBirth
                                                            ).toLocaleDateString()
                                                          )
                                                        : "—"
                                                    }
                                                  />
                                                </div>
                                              </section>

                                              {/* ===== EMPLOYMENT DETAILS ===== */}
                                              <section>
                                                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                  <span className="h-4 w-1 rounded bg-emerald-600" />
                                                  Employment Details
                                                </h3>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                  <InfoRow label="Position" value={data.position} />
                                                  <InfoRow
                                                    label="Grade Level"
                                                    value={data.gradeLevel}
                                                  />
                                                  <InfoRow
                                                    label="OG Number"
                                                    value={data.ogNumber}
                                                  />
                                                  <InfoRow
                                                    label="TSC File No."
                                                    value={data.tscFileNumber}
                                                  />
                                                  <InfoRow
                                                    label="First Appointment"
                                                    value={
                                                      data.dateOfFirstAppointment
                                                        ? getLongDate(
                                                            new Date(
                                                              data.dateOfFirstAppointment
                                                            ).toLocaleDateString()
                                                          )
                                                        : "—"
                                                    }
                                                  />
                                                  <InfoRow
                                                    label="Date of Retirement"
                                                    value={
                                                      data.dateOfRetirement
                                                        ? getLongDate(
                                                            new Date(
                                                              data.dateOfRetirement
                                                            ).toLocaleDateString()
                                                          )
                                                        : "—"
                                                    }
                                                  />
                                                  <InfoRow
                                                    label="School of Previous Posting"
                                                    value={
                                                      data.schoolOfPreviousPosting?.nameOfSchool
                                                    }
                                                  />
                                                  <InfoRow
                                                    label="Date of Present School Posting"
                                                    value={
                                                      data.dateOfPresentSchoolPosting
                                                        ? getLongDate(
                                                            data.dateOfPresentSchoolPosting
                                                          )
                                                        : "—"
                                                    }
                                                  />
                                                </div>
                                              </section>

                                              {/* ===== QUALIFICATIONS ===== */}
                                              <section>
                                                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                  <span className="h-4 w-1 rounded bg-emerald-600" />
                                                  Qualifications
                                                </h3>

                                                {data?.qualifications?.length ? (
                                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {data.qualifications.map(
                                                      (q: any, i: number) => (
                                                        <div
                                                          key={i}
                                                          className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-1"
                                                        >
                                                          <p>
                                                            <span className="font-semibold">
                                                              Institution:
                                                            </span>{" "}
                                                            {q.schoolName}
                                                          </p>
                                                          <p>
                                                            <span className="font-semibold">
                                                              Degree:
                                                            </span>{" "}
                                                            {q.degreeType}
                                                          </p>
                                                          <p>
                                                            <span className="font-semibold">
                                                              Specialization:
                                                            </span>{" "}
                                                            {q.specialization}
                                                          </p>
                                                          <p>
                                                            <span className="font-semibold">
                                                              Years:
                                                            </span>{" "}
                                                            {q.startYear} – {q.endYear}
                                                          </p>
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                ) : (
                                                  <p className="italic text-gray-500">
                                                    No qualifications recorded
                                                  </p>
                                                )}
                                              </section>

                                              {/* ===== DOCUMENTS ===== */}
                                              <section>
                                                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                  <span className="h-4 w-1 rounded bg-emerald-600" />
                                                  Documents
                                                </h3>

                                                <div className="flex items-center gap-4">
                                                  <span className="font-medium">
                                                    Posting Letter:
                                                  </span>

                                                  {data?.letters?.postingLetter ? (
                                                    <a
                                                      href={data.letters.postingLetter}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 transition shadow"
                                                    >
                                                      <img
                                                        src={downloadLogo}
                                                        alt="Download posting letter"
                                                        className="w-7 h-7"
                                                      />
                                                    </a>
                                                  ) : (
                                                    <span className="text-gray-500">
                                                      Not available
                                                    </span>
                                                  )}
                                                </div>
                                              </section>
                                            </div>
                                          )}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* STAFF LIST DROPDOWN */}
                    <div className="mt-4 flex-1 bg-green-100 rounded-2xl shadow-xl border border-gray-700">
                      <button
                        type="button"
                        onClick={() => school._id && toggleStaffList(school._id)}
                        className="w-full flex justify-between items-center p-6 hover:bg-green-200 rounded-2xl transition-colors"
                      >
                        <div>
                          <h5 className="text-xl font-semibold text-emerald-600 tracking-wide">
                            List of Staff
                          </h5>
                          <span className="text-sm text-gray-600">
                            {school?.listOfStaff?.length || 0} Staff
                          </span>
                        </div>
                        <span className="text-2xl font-bold text-emerald-600">
                          {showStaffList[school._id] ? "−" : "+"}
                        </span>
                      </button>

                      <AnimatePresence>
                        {showStaffList[school._id] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-0">
                              {/* SEARCH */}
                              <div className="relative w-full md:w-[320px] mb-6">
                                <Input
                                  size="sm"
                                  value={staffSearch}
                                  onChange={(e: any) => setStaffSearch(e.target.value)}
                                  placeholder="Search staff name or OG Number..."
                                  className="h-10 rounded-full pl-4 pr-10 text-sm bg-white shadow"
                                />
                                <TesSearch
                                  size={18}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                />
                              </div>

                              {/* STAFF DROPDOWN LIST */}
                              {school?.listOfStaff?.length ? (
                                <ul className="space-y-3">
                                  {school.listOfStaff
                                    .filter(
                                      (staff: any) =>
                                        staff?.staffName?.firstName
                                          ?.toLowerCase()
                                          .includes(staffSearch.toLowerCase()) ||
                                        staff?.ogNumber
                                          ?.toLowerCase()
                                          .includes(staffSearch.toLowerCase())
                                    )
                                    .map((staff: any) => (
                                      <li
                                        key={staff?._id}
                                        className="bg-white border border-gray-300 rounded-xl shadow-sm"
                                      >
                                        {/* STAFF HEADER (CLICK TO TOGGLE) */}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setOpenStaffId(
                                              openStaffId === staff._id ? null : staff._id
                                            )
                                          }
                                          className="w-full flex justify-between items-center px-4 py-3 hover:bg-emerald-50 rounded-xl"
                                        >
                                          <div className="text-left">
                                            <p className="font-semibold text-gray-800">
                                              {staff?.staffName?.firstName || "Unnamed Staff"}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {staff?.ogNumber || "—"} • {staff?.staffType || "—"}
                                            </p>
                                          </div>

                                          <span className="text-xl font-bold text-emerald-600">
                                            {openStaffId === staff._id ? "−" : "+"}
                                          </span>
                                        </button>

                                        {/* STAFF DETAILS DROPDOWN */}
                                        <AnimatePresence>
                                          {openStaffId === staff._id && (
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: "auto", opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.25 }}
                                              className="overflow-hidden px-4 pb-4"
                                            >
                                              <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200">
                                                <div className="px-4 py-3 border-b border-gray-200">
                                                  <h3 className="text-lg font-semibold text-black">
                                                    Staff Details
                                                  </h3>
                                                </div>
                                                <div className="mt-4 space-y-6 text-sm text-gray-700">
                                                  {/* ===== PERSONAL INFORMATION ===== */}
                                                  <section>
                                                    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                      <span className="h-4 w-1 rounded bg-emerald-600" />
                                                      Personal Information
                                                    </h3>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                      <InfoRow
                                                        label="First Name"
                                                        value={staff?.staffName?.firstName}
                                                      />
                                                      <InfoRow
                                                        label="Gender"
                                                        value={staff?.gender}
                                                      />
                                                      <InfoRow
                                                        label="Phone Number"
                                                        value={staff?.phoneNumber}
                                                      />
                                                      <InfoRow
                                                        label="Residential Address"
                                                        value={staff?.residentialAddress}
                                                      />
                                                      <InfoRow
                                                        label="Date of Birth"
                                                        value={
                                                          staff?.dateOfBirth
                                                            ? getLongDate(
                                                                new Date(
                                                                  staff.dateOfBirth
                                                                ).toLocaleDateString()
                                                              )
                                                            : "—"
                                                        }
                                                      />
                                                    </div>
                                                  </section>

                                                  {/* ===== EMPLOYMENT DETAILS ===== */}
                                                  <section>
                                                    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                      <span className="h-4 w-1 rounded bg-emerald-600" />
                                                      Employment Details
                                                    </h3>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                      <InfoRow
                                                        label="Position"
                                                        value={staff?.position || "Staff"}
                                                      />
                                                      <InfoRow
                                                        label="Grade Level"
                                                        value={staff?.gradeLevel}
                                                      />
                                                      <InfoRow
                                                        label="OG Number"
                                                        value={staff?.ogNumber}
                                                      />
                                                      <InfoRow
                                                        label="TSC File No."
                                                        value={staff?.tscFileNumber}
                                                      />

                                                      <InfoRow
                                                        label="First Appointment"
                                                        value={
                                                          staff?.dateOfFirstAppointment
                                                            ? getLongDate(
                                                                new Date(
                                                                  staff.dateOfFirstAppointment
                                                                ).toLocaleDateString()
                                                              )
                                                            : "—"
                                                        }
                                                      />

                                                      <InfoRow
                                                        label="Date of Retirement"
                                                        value={
                                                          staff?.dateOfRetirement
                                                            ? getLongDate(
                                                                new Date(
                                                                  staff.dateOfRetirement
                                                                ).toLocaleDateString()
                                                              )
                                                            : "—"
                                                        }
                                                      />

                                                      <InfoRow
                                                        label="School of Previous Posting"
                                                        value={
                                                          staff?.schoolOfPreviousPosting
                                                            ?.nameOfSchool
                                                        }
                                                      />

                                                      <InfoRow
                                                        label="Date of Present School Posting"
                                                        value={
                                                          staff?.dateOfPresentSchoolPosting
                                                            ? getLongDate(
                                                                new Date(
                                                                  staff.dateOfPresentSchoolPosting
                                                                ).toLocaleDateString()
                                                              )
                                                            : "—"
                                                        }
                                                      />
                                                    </div>
                                                  </section>

                                                  {/* ===== SUBJECTS TAUGHT ===== */}
                                                  <section>
                                                    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                      <span className="h-4 w-1 rounded bg-emerald-600" />
                                                      Subjects Taught
                                                    </h3>

                                                    {staff?.subjectsTaught?.length ? (
                                                      <div className="flex flex-wrap gap-2">
                                                        {staff.subjectsTaught.map(
                                                          (s: string, i: number) => (
                                                            <span
                                                              key={i}
                                                              className="px-3 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 font-medium"
                                                            >
                                                              {s}
                                                            </span>
                                                          )
                                                        )}
                                                      </div>
                                                    ) : (
                                                      <p className="text-gray-500 italic">—</p>
                                                    )}
                                                  </section>

                                                  {/* ===== QUALIFICATIONS ===== */}
                                                  <section>
                                                    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                      <span className="h-4 w-1 rounded bg-emerald-600" />
                                                      Qualifications
                                                    </h3>

                                                    {staff?.qualifications?.length ? (
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {staff.qualifications.map(
                                                          (q: any, i: number) => (
                                                            <div
                                                              key={i}
                                                              className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-1"
                                                            >
                                                              <p>
                                                                <span className="font-semibold">
                                                                  Institution:
                                                                </span>{" "}
                                                                {q.schoolName}
                                                              </p>
                                                              <p>
                                                                <span className="font-semibold">
                                                                  Degree:
                                                                </span>{" "}
                                                                {q.degreeType}
                                                              </p>
                                                              <p>
                                                                <span className="font-semibold">
                                                                  Specialization:
                                                                </span>{" "}
                                                                {q.specialization}
                                                              </p>
                                                              <p>
                                                                <span className="font-semibold">
                                                                  Years:
                                                                </span>{" "}
                                                                {q.startYear} – {q.endYear}
                                                              </p>
                                                            </div>
                                                          )
                                                        )}
                                                      </div>
                                                    ) : (
                                                      <p className="italic text-gray-500">
                                                        No qualifications recorded
                                                      </p>
                                                    )}
                                                  </section>

                                                  {/* ===== DOCUMENTS ===== */}
                                                  <section>
                                                    <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-emerald-700">
                                                      <span className="h-4 w-1 rounded bg-emerald-600" />
                                                      Documents
                                                    </h3>

                                                    <div className="flex items-center gap-4">
                                                      <span className="font-medium">
                                                        Posting Letter:
                                                      </span>

                                                      {staff?.letters?.postingLetter ? (
                                                        <a
                                                          href={staff.letters.postingLetter}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 transition shadow"
                                                        >
                                                          <img
                                                            src={downloadLogo}
                                                            alt="Download posting letter"
                                                            className="w-7 h-7"
                                                          />
                                                        </a>
                                                      ) : (
                                                        <span className="text-gray-500">
                                                          Not available
                                                        </span>
                                                      )}
                                                    </div>
                                                  </section>
                                                </div>
                                              </div>
                                            </motion.div>
                                          )}
                                        </AnimatePresence>
                                      </li>
                                    ))}
                                </ul>
                              ) : (
                                <p className="text-gray-500 italic">No staff members found.</p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        ) : (
          <p className="text-white text-center">No schools found matching your search.</p>
        )}

        {showPrevNext && (
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePreviousSchool}
              className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#1b733f]/80"
            >
              Previous School
            </button>
            <span className="text-white">
              School {currentSchoolIndex + 1} of {filteredSchools.length}
            </span>
            {currentSchoolIndex < filteredSchools.length - 1 && (
              <button
                onClick={handleNextSchool}
                className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Next School
              </button>
            )}
          </div>
        )}

        {!showPrevNext && (
          <div className="flex justify-center mt-4">
            {currentPage !== 1 && (
              <button
                onClick={() => {
                  handlePageChange(currentPage - 1);
                }}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-l shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Previous
              </button>
            )}
            <span className="px-4 py-2 bg-[#1b733f] text-white shadow-md">
              Page {currentPage} of {totalPages}
            </span>
            {currentPage !== totalPages && (
              <button
                onClick={() => {
                  handlePageChange(currentPage + 1);
                }}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-r shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
